import React, { useState, useEffect } from 'react'
import Navigation from '../components/navigation/Navigation'
import Dashboard from './Dashboard'
import Products from './Products'
import Purchases from './Purchases'
import Settings from './Settings'
import LoadingOverlay from '../components/common/LoadingOverlay'
import AuthFlow from '../components/auth/AuthFlow'
import { loadFromStorage, dexieService } from '../utils/storage'
import authService from '../services/authService'
import apiService from '../services/apiService'
import jwtUtils from '../utils/jwtUtils'
import '../styles/base.scss'
import '../styles/loading.scss'
import './Accueil.scss'
import managerService from '../services/managerService'
import { applyTheme } from '../utils/theme-switch'
import PWAInstallPrompt from '../components/common/PWAInstallPrompt'
import logoOGC from '../assets/images/logo.png'

const Accueil = () => {
  const [currentTab, setCurrentTab] = useState('dashboard')
  const [products, setProducts] = useState([])
  const [purchases, setPurchases] = useState([])
  const [nextProductId, setNextProductId] = useState(1)
  const [nextClientId, setNextClientId] = useState(1)
  const [shopInfo, setShopInfo] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [authChecking, setAuthChecking] = useState(true)
  const [goRenewData, setGoRenewData] = useState({})
  const [userRole, setUserRole] = useState('')
  const [showPWAPrompt, setShowPWAPrompt] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [isInStandaloneMode, setIsInStandaloneMode] = useState(false)

  const [loading, setLoading] = useState({
    initial: true,
    saving: false,
    deleting: null,
    importing: false,
    exporting: false,
  })

  const [dashboardDateFilter, setDashboardDateFilter] = useState('today')
  const [dashboardStartDate, setDashboardStartDate] = useState('')
  const [dashboardEndDate, setDashboardEndDate] = useState('')

  // AFFICHAGE DU POPUP D'INSTALLATION
  // ON DESACTIVE LE BOUTON INSTALLER SUR FIREFOX POUR CAUSE DE BUG
  const isFirefox = navigator.userAgent.toLowerCase().includes('firefox')
  const [isVisiblePopupInstallApp, setIsVisiblePopupInstallApp] =
    useState(false)
  useEffect(() => {
    // Afficher le popup après 3 secondes
    const timer = setTimeout(() => {
      setIsVisiblePopupInstallApp(true)
    }, 3000)

    return () => {
      clearTimeout(timer)
    }
  }, [])

  // Détection de l'installation PWA
  useEffect(() => {
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
    const isInStandaloneMode1 =
      'standalone' in window.navigator && window.navigator.standalone
    setIsInStandaloneMode(isInStandaloneMode1)

    // Si iOS et pas en mode standalone, afficher le guide après 3 secondes
    if (isIOS && !isInStandaloneMode1) {
      const hasDeclinedInstall = localStorage.getItem('pwa-install-declined')
      const declineTime = localStorage.getItem('pwa-install-declined-time')

      process.env.REACT_APP_NODE_ENV === 'development' &&
        console.log('iOS Decline status:', { hasDeclinedInstall, declineTime })

      // Vérifier si les 7 jours sont passés
      if (hasDeclinedInstall && declineTime) {
        const daysSinceDecline =
          (Date.now() - parseInt(declineTime)) / (1000 * 60 * 60 * 24)
        process.env.REACT_APP_NODE_ENV === 'development' &&
          console.log('Days since decline:', daysSinceDecline)
        if (daysSinceDecline < 7) {
          process.env.REACT_APP_NODE_ENV === 'development' &&
            console.log('⏰ Less than 7 days, not showing prompt')
          return
        }
      }

      // process.env.REACT_APP_NODE_ENV === 'development' &&
      //   console.log('📱 iOS detected, showing guide in 3 seconds...')
      // const timer = setTimeout(() => {
      //   process.env.REACT_APP_NODE_ENV === 'development' &&
      //     console.log('🎉 Displaying iOS PWA guide!')
      //   setShowPWAPrompt(true)
      // }, 3000)

      // return () => clearTimeout(timer)
    }

    // Capturer l'événement beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault()
      setDeferredPrompt(e)

      // Vérifier si l'utilisateur a déjà refusé l'installation
      const hasDeclinedInstall = localStorage.getItem('pwa-install-declined')
      const declineTime = localStorage.getItem('pwa-install-declined-time')

      // Vérifier si les 7 jours sont passés
      if (hasDeclinedInstall && declineTime) {
        const daysSinceDecline =
          (Date.now() - parseInt(declineTime)) / (1000 * 60 * 60 * 24)
        if (daysSinceDecline < 7) {
          return // Ne pas afficher si moins de 7 jours
        }
      }

      // Attendre 3 secondes après le chargement avant d'afficher le prompt
      // setTimeout(() => {
      //   setShowPWAPrompt(true)
      // }, 3000)
    }

    // Vérifier si l'app est déjà installée
    const isStandalone2 =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone ||
      document.referrer.includes('android-app://')
    setIsInStandaloneMode(isStandalone2)

    if (!isStandalone2 && !isIOS) {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt,
      )
    }
  }, [])

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    setAuthChecking(true)

    const token = authService.getToken()

    if (!token || jwtUtils.isTokenExpired(token)) {
      authService.logout()
      setIsAuthenticated(false)
      setAuthChecking(false)
      return
    }

    const tokenInfo = jwtUtils.extractTokenInfo(token)
    setUserRole(tokenInfo.role)

    const connected = await authService.isOnline()
    if (connected) {
      try {
        const response = await apiService.verifyToken(token)

        if (response.success) {
          setShopInfo(response.data.shop)
          setIsAuthenticated(true)
          await loadInitialData()
        } else {
          authService.logout()
          setIsAuthenticated(false)
        }
      } catch (error) {
        console.error('Erreur vérification token:', error)
        const tokenInfo = authService.getTokenInfo()
        if (tokenInfo) {
          const savedShopInfo = await dexieService.getShopInfo()
          setShopInfo(
            savedShopInfo || {
              shopName: 'Ma Boutique',
              email: tokenInfo.email,
              plan: tokenInfo.plan,
              maxSessions: tokenInfo.maxSessions,
            },
          )
          setIsAuthenticated(true)
          await loadInitialData()
        } else {
          authService.logout()
          setIsAuthenticated(false)
        }
      }
    } else {
      const tokenInfo = authService.getTokenInfo()
      if (tokenInfo) {
        const savedShopInfo = await dexieService.getShopInfo()
        setShopInfo(
          savedShopInfo || {
            shopName: 'Ma Boutique',
            email: tokenInfo.email,
            plan: tokenInfo.plan,
            maxSessions: tokenInfo.maxSessions,
          },
        )
        setIsAuthenticated(true)
        await loadInitialData()
      } else {
        authService.logout()
        setIsAuthenticated(false)
      }
    }

    setAuthChecking(false)
  }

  useEffect(() => {
    if (!isAuthenticated) return

    const checkTokenPeriodically = setInterval(
      async () => {
        const token = authService.getToken()
        if (!token || jwtUtils.isTokenExpired(token)) {
          alert('Votre session a expiré. Veuillez vous reconnecter.')
          handleLogout()
          return
        }

        const daysLeft = authService.getDaysUntilExpiry()
        if (daysLeft <= 0) {
          alert(
            'Votre abonnement a expiré. Veuillez le renouvelé pour profiter pleinement de votre espace.',
          )
          handleLogout()
          return
        }

        const connected = await authService.isOnline()
        if (connected) {
          try {
            const response = await apiService.verifyToken(token)

            if (!response.success) {
              if (response.code === 'PLAN_EXPIRED') {
                alert(
                  'Votre abonnement a expiré. Veuillez renouveler votre plan.',
                )
              } else if (response.code === 'INVALID_SESSION') {
                alert('Votre session a été fermée. Veuillez vous reconnecter.')
              }
              handleLogout()
            }
          } catch (error) {
            console.error('Erreur vérification périodique:', error)
          }
        }
      },
      5 * 60 * 1000,
    )

    return () => clearInterval(checkTokenPeriodically)
  }, [isAuthenticated])

  const loadInitialData = async () => {
    setLoading((prev) => ({ ...prev, initial: true }))

    try {
      await new Promise((resolve) => setTimeout(resolve, 1500))

      const data = await loadFromStorage()
      setProducts(data.products)
      setPurchases(data.purchases)
      setNextProductId(data.nextProductId)
      setNextClientId(data.nextClientId)

      if (data.shopInfo) {
        setShopInfo((prev) => ({ ...prev, ...data.shopInfo }))
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error)
    } finally {
      setLoading((prev) => ({ ...prev, initial: false }))
    }
  }

  const handlePWAInstall = async () => {
    if (!deferredPrompt) {
      process.env.REACT_APP_NODE_ENV === 'development' &&
        console.log('Installation prompt not available')
      return
    }

    try {
      deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice

      if (outcome === 'accepted') {
        process.env.REACT_APP_NODE_ENV === 'development' &&
          console.log('PWA installée avec succès')
      }

      setDeferredPrompt(null)
      setShowPWAPrompt(false)
      setIsInStandaloneMode(true)
    } catch (error) {
      process.env.REACT_APP_NODE_ENV === 'development' &&
        console.error('Erreur installation PWA:', error)
    }
  }

  const handlePWAClose = () => {
    setShowPWAPrompt(false)
    // Stocker la préférence pour ne pas redemander pendant 7 jours
    localStorage.setItem('pwa-install-declined', 'true')
    localStorage.setItem('pwa-install-declined-time', Date.now().toString())
  }

  const handleAuthSuccess = async (authData) => {
    const { token, shop } = authData

    await authService.saveToken(token)

    const tokenInfo = jwtUtils.extractTokenInfo(token)
    setUserRole(tokenInfo.role)

    setShopInfo(shop)
    await dexieService.saveShopInfo(shop)

    setIsAuthenticated(true)

    await loadInitialData()
  }

  const handleLogout = async () => {
    const connected = await authService.isOnline()
    if (!connected) {
      alert(
        'Déconnexion impossible. Veuillez vérifier votre connexion internet pour que nous puissions vous déconnecter correctement.',
      )
    } else {
      try {
        const token = authService.getToken()
        await apiService.logout(token)
      } catch (error) {
        console.error('Erreur déconnexion API:', error)
      }
      authService.logout()
      setIsAuthenticated(false)
      setShopInfo(null)
      setProducts([])
      setPurchases([])
      setUserRole('')
    }
  }

  const handleProductSave = async (productData, existingProduct) => {
    setLoading((prev) => ({ ...prev, saving: true }))

    try {
      await new Promise((resolve) => setTimeout(resolve, 600))

      if (existingProduct) {
        await dexieService.updateProduct(existingProduct.id, productData)

        setProducts((prev) =>
          prev.map((p) =>
            p.id === existingProduct.id
              ? { ...productData, id: existingProduct.id }
              : p,
          ),
        )
      } else {
        const newId = await dexieService.addProduct(productData)
        const newProduct = { ...productData, id: newId }

        setProducts((prev) => [newProduct, ...prev])
        setNextProductId((prev) => prev + 1)
        await dexieService.saveMetadata('nextProductId', nextProductId + 1)
      }
    } catch (error) {
      console.error('Erreur sauvegarde produit:', error)
      alert('Erreur lors de la sauvegarde')
    } finally {
      setLoading((prev) => ({ ...prev, saving: false }))
    }
  }

  const handleProductDelete = async (productId) => {
    setLoading((prev) => ({ ...prev, deleting: productId }))

    try {
      await new Promise((resolve) => setTimeout(resolve, 600))

      await dexieService.deleteProduct(productId)
      setProducts((prev) => prev.filter((p) => p.id !== productId))
    } catch (error) {
      console.error('Erreur suppression:', error)
    } finally {
      setLoading((prev) => ({ ...prev, deleting: null }))
    }
  }

  const handlePurchaseSave = async (purchaseData, existingPurchase) => {
    setLoading((prev) => ({ ...prev, saving: true }))

    try {
      await new Promise((resolve) => setTimeout(resolve, 800))

      if (existingPurchase) {
        await dexieService.updatePurchase(existingPurchase.id, purchaseData)

        setPurchases((prev) =>
          prev.map((p) =>
            p.id === existingPurchase.id
              ? { ...purchaseData, id: existingPurchase.id }
              : p,
          ),
        )
      } else {
        const newId = await dexieService.addPurchase(purchaseData)
        const newPurchase = { ...purchaseData, id: newId }

        setPurchases((prev) => [newPurchase, ...prev])
        setNextClientId((prev) => prev + 1)
        await dexieService.saveMetadata('nextClientId', nextClientId + 1)
      }
    } catch (error) {
      console.error('Erreur sauvegarde achat:', error)
      alert('Erreur lors de la sauvegarde')
    } finally {
      setLoading((prev) => ({ ...prev, saving: false }))
    }
  }

  const handleImportSuccess = async () => {
    const data = await loadFromStorage()
    setProducts(data.products)
    setPurchases(data.purchases)
    setNextProductId(data.nextProductId)
    setNextClientId(data.nextClientId)
  }

  const handleFormatShop = async () => {
    const confirmed = window.confirm(
      'ATTENTION : Cette action supprimera définitivement tous les produits et achats de cette boutique. Cette action est irréversible.\n\nÊtes-vous absolument sûr de vouloir continuer ?',
    )

    if (!confirmed) return

    const doubleConfirm = window.confirm(
      'Dernière confirmation : Voulez-vous vraiment supprimer TOUTES les données de votre boutique ?',
    )

    if (!doubleConfirm) return

    setLoading((prev) => ({ ...prev, saving: true }))

    try {
      const success = await dexieService.formatShop()

      if (success) {
        setProducts([])
        setPurchases([])
        setNextProductId(1)
        setNextClientId(1)
        await dexieService.saveMetadata('nextProductId', 1)
        await dexieService.saveMetadata('nextClientId', 1)
        alert('Boutique formatée avec succès')
      } else {
        alert('Erreur lors du formatage')
      }
    } catch (error) {
      console.error('Erreur formatage:', error)
      alert('Erreur lors du formatage')
    } finally {
      setLoading((prev) => ({ ...prev, saving: false }))
    }
  }

  if (authChecking) {
    return (
      <LoadingOverlay isVisible={true} text='Vérification de la session...' />
    )
  }

  const goToRenewSubscription = (value) => {
    setGoRenewData(value)
  }

  if (!isAuthenticated || goRenewData.renewSubscription) {
    applyTheme('page1')
    return (
      <>
        <AuthFlow
          onAuthSuccess={handleAuthSuccess}
          goRenew={goRenewData}
          cancleRenew={goToRenewSubscription}
        />

        {/* Popup invite install app  */}
        {!isInStandaloneMode && !isFirefox && (
          <div
            className={`install-popup ${isVisiblePopupInstallApp ? 'install-popup--visible' : ''}`}
          >
            <div className='install-popup__content'>
              <img src={logoOGC} alt='Logo' className='install-popup__logo' />
              <div className='install-popup__text'>
                <h3 className='install-popup__title'>Installez OGC224</h3>
                <p className='install-popup__description'>
                  Profitez d'une expérience optimale, même hors ligne.
                </p>
              </div>
              <div className='install-popup__actions'>
                <button
                  className='install-popup__btn install-popup__btn--primary'
                  onClick={() => setShowPWAPrompt(true)}
                >
                  Installer
                </button>
                <button
                  className='install-popup__btn install-popup__btn--secondary'
                  onClick={() => setIsVisiblePopupInstallApp(false)}
                >
                  Plus tard
                </button>
              </div>
            </div>
          </div>
        )}

        {showPWAPrompt && (
          <PWAInstallPrompt
            onClose={handlePWAClose}
            onInstall={handlePWAInstall}
          />
        )}
      </>
    )
  } else {
    applyTheme('page2')
  }

  // Protection des tabs
  const canAccessTab = (tabName) => {
    return managerService.canAccessTab(tabName)
  }

  return (
    <>
      {showPWAPrompt && (
        <PWAInstallPrompt
          onClose={handlePWAClose}
          onInstall={handlePWAInstall}
        />
      )}

      <div className='app-container'>
        <LoadingOverlay
          isVisible={loading.saving}
          text='Sauvegarde en cours...'
        />

        <Navigation
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          shopInfo={shopInfo}
          isInStandaloneMode={isInStandaloneMode}
          installed={() => setShowPWAPrompt(true)}
        />

        <main
          className={`main-content ${userRole !== 'manager' ? 'main-content-isAdminLog' : ''}`}
        >
          {canAccessTab('dashboard') && currentTab === 'dashboard' && (
            <Dashboard
              products={products}
              purchases={purchases}
              dashboardDateFilter={dashboardDateFilter}
              setDashboardDateFilter={setDashboardDateFilter}
              dashboardStartDate={dashboardStartDate}
              setDashboardStartDate={setDashboardStartDate}
              dashboardEndDate={dashboardEndDate}
              setDashboardEndDate={setDashboardEndDate}
            />
          )}

          {canAccessTab('products') && currentTab === 'products' && (
            <Products
              products={products}
              purchases={purchases}
              onProductSave={handleProductSave}
              onProductDelete={handleProductDelete}
              loading={loading}
              nextProductId={nextProductId}
            />
          )}

          {canAccessTab('purchases') && currentTab === 'purchases' && (
            <Purchases
              shopInfo={shopInfo}
              products={products}
              purchases={purchases}
              onPurchaseSave={handlePurchaseSave}
              loading={loading}
              nextClientId={nextClientId}
            />
          )}

          {currentTab === 'settings' && (
            <Settings
              shopInfo={shopInfo}
              products={products}
              purchases={purchases}
              loading={loading}
              onImportSuccess={handleImportSuccess}
              onLogout={handleLogout}
              onFormatShop={handleFormatShop}
              isRenewSubscription={goToRenewSubscription}
              userRole={userRole}
            />
          )}
        </main>
      </div>
    </>
  )
}

export default Accueil
